import React from "react";
import { observer } from "mobx-react";
import copilotStore, { TABLE_PAGE_SIZES } from "Stores/copilotStore";
import PaginationComponent from "Components/common/PaginationComponent";
import ProspectTable from "./ProspectTable";

// LAZY: ContactActionBar reuses the Contacts page's buttons and popups, so a
// static import would pull that page's module tree into the copilot bundle —
// a throw during its module init would take the whole panel down silently.
const ContactActionBar = React.lazy(() => import("./ContactActionBar"));

const TableSidePanel = observer(() => {
  const { tableId } = copilotStore.sidePanel;
  const table = copilotStore.tables.get(tableId);
  if (!table) return null;

  const totalPages = Math.max(1, Math.ceil(table.totalCount / table.pageSize));
  const selectedCount = table.allMatchingSelected
    ? table.totalCount
    : (table.selectedIds || []).length;
  // Only worth offering once this page is fully ticked and more exist beyond it.
  const pageIds = (table.rows || []).map(row => row.id).filter(Boolean);
  const pageAllSelected =
    pageIds.length > 0 &&
    pageIds.every(id => (table.selectedIds || []).indexOf(id) >= 0);
  // Never offered for page-scoped tables: prospect rows cost credits to
  // acquire, so selection stops at what is on screen.
  const offerAllMatching =
    table.selectionScope !== "page" &&
    pageAllSelected &&
    table.totalCount > pageIds.length;

  return (
    <>
      <div className="copilotTablePanel">
        <div className="copilotTablePanel__header">
          <div className="copilotTablePanel__headMeta">
            <div className="copilotTablePanel__title">{table.title}</div>
            <div className="copilotTablePanel__count">
              {table.totalCount.toLocaleString()} records
            </div>
          </div>
          <span
            className="material-icons-outlined copilotTablePanel__close"
            onClick={copilotStore.closeTablePanel}
          >
            close
          </span>
        </div>
        <div className="copilotTablePanel__body">
          {table.stale ? (
            <div className="copilotTablePanel__stale">
              <i className="material-icons-outlined">history</i>
              <div className="copilotTablePanel__staleTitle">
                {table.kind && table.args
                  ? "Couldn't load these results"
                  : "Results no longer available"}
              </div>
              <div className="copilotTablePanel__staleText">
                {table.kind && table.args
                  ? "The search that built this table can be run again to bring the same results back."
                  : "This table is from an earlier chat and its results aren't kept once the chat ends. Ask Copilot to run the search again to see and act on these results."}
              </div>
              {table.kind && table.args && (
                <button
                  type="button"
                  className="copilotTablePanel__staleReload"
                  disabled={table.loading}
                  onClick={() => copilotStore.retryStaleTable(tableId)}
                >
                  <i className="material-icons-outlined">refresh</i>
                  Reload results
                </button>
              )}
            </div>
          ) : (
            <>
              {table.selectable && (
                /* Reserved: the bar swaps in over a hint of the same height,
                   so ticking a row never reflows the rows under the cursor. */
                <div className="copilotTablePanel__selection">
                  {selectedCount > 0 ? (
                    <React.Suspense fallback={null}>
                      <ContactActionBar tableId={tableId} />
                    </React.Suspense>
                  ) : (
                    <span className="copilotTablePanel__selectionHint">
                      Select rows to act on them.
                    </span>
                  )}
                  {offerAllMatching && (
                    <span className="copilotTablePanel__selectAll">
                      {table.allMatchingSelected ? (
                        <button
                          type="button"
                          onClick={() =>
                            copilotStore.selectAllMatching(tableId, false)
                          }
                        >
                          Clear selection of all{" "}
                          {table.totalCount.toLocaleString()}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            copilotStore.selectAllMatching(tableId, true)
                          }
                        >
                          Select all {table.totalCount.toLocaleString()} matching
                        </button>
                      )}
                    </span>
                  )}
                </div>
              )}
              <ProspectTable
                table={table}
                tableId={tableId}
                onRowClick={
                  table.selectable
                    ? row => copilotStore.openContactFromTable(tableId, row)
                    : undefined
                }
              />
            </>
          )}
        </div>
        {!table.stale && (
          <div className="copilotTablePanel__footer">
            <div className="copilotTablePanel__pageSize">
              <span className="copilotTablePanel__pageSizeLabel">Rows</span>
              {TABLE_PAGE_SIZES.map(size => (
                <button
                  type="button"
                  key={size}
                  className={`copilotTablePanel__pageSizeBtn ${
                    table.pageSize === size ? "selected" : ""
                  }`}
                  disabled={table.loading}
                  onClick={() => copilotStore.setTablePageSize(tableId, size)}
                >
                  {size}
                </button>
              ))}
            </div>
            <PaginationComponent
              totalPages={totalPages}
              currentPage={table.currentPage}
              loading={table.loading}
              callback={({ selected }) =>
                copilotStore.fetchTablePage(tableId, selected + 1)
              }
            />
          </div>
        )}
      </div>
    </>
  );
});

export default TableSidePanel;
